import {Component, computed, EventEmitter, inject, Input, OnDestroy, Output, signal} from '@angular/core';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {AutoFocus} from 'primeng/autofocus';
import {ShortcutContext} from '../../../../shared/shortcuts/shortcut.context';

type InlineFieldType = 'text' | 'select' | 'date';

@Component({
	selector: 'app-inline-field',
	standalone: true,
	templateUrl: './inline-field.html',
	imports: [
		Button,
		InputText,
		Select,
		AutoFocus
	],
	styles: `
	.detail-user {
		display: grid;
		grid-template-columns: .3fr 1fr;
		align-items: center;
	}
	`
})
export class InlineFieldComponent implements OnDestroy{
	readonly shortcuts = inject(ShortcutContext)

	@Input({ required: true }) label!: string;
	@Input() value: any;
	@Input() type: InlineFieldType = 'text';
	@Input() editable = false;
	@Input() disabled = false;
	@Input() options?: { label: string; value: string }[]

	@Output() save = new EventEmitter<any>()
	@Output() cancel = new EventEmitter<void>()

	private editing = signal(false)
	readonly draftValue = signal<any>(null)

	isEditing = computed( () => this.editing() )

	ngOnDestroy() {
		this.clearShortcuts();
	}

	startEdit(): void {
		if (!this.editable || this.disabled) return;

		this.draftValue.set(this.value);
		this.editing.set(true);

		this.shortcuts.register({
			id: `inline-save-${this.label}`,
			description: 'Salva',
			keys: ['ENTER'],
			display: [
				{kind: 'icon', name: 'pi-reply rotate-180'}
			],
			action: () => this.onSave(),
			showInFooter: true,
			showInHelp: false,
			scope: 'dialog'
		});

		this.shortcuts.register({
			id: `inline-cancel-${this.label}`,
			description: 'Annulla',
			keys: ['ESCAPE'],
			display: [
				{kind: 'key', value: 'Esc'}
			],
			action: () => this.onCancel(),
			showInFooter: true,
			showInHelp: false,
			scope: 'dialog'
		});

		this.draftValue.set(this.value);
		this.editing.set(true);
	}

	private clearShortcuts() {
		this.shortcuts.unregister(`inline-save-${this.label}`);
		this.shortcuts.unregister(`inline-cancel-${this.label}`);
	}

	onSave(): void {
		this.save.emit(this.draftValue());
		this.editing.set(false);
		this.clearShortcuts();
	}

	onCancel(): void {
		this.draftValue.set(this.value);
		this.editing.set(false);
		this.clearShortcuts();
		this.cancel.emit();
	}

	handleEscape(event: Event) {
		if (!(event instanceof KeyboardEvent)) return;

		event.preventDefault();
		event.stopPropagation();
		this.onCancel();
	}

	updateDraft(value: any): void {
		this.draftValue.set(value);
	}

	get displayValue(): string {
		if (this.type === 'select' && this.options) {
			const opt = this.options.find(o => o.value === this.value);
			return opt?.label ?? '-';
		}
		return this.value ?? '-';
	}
}
